import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { History, Save, Trash2, Plus, EyeOff, Eye, Image as ImageIcon, FolderOpen } from "lucide-react";
import { invalidateContentCache } from "@/hooks/useContent";
import MediaPicker from "@/components/admin/MediaPicker";
import dayjs from "dayjs";

const PAGES = [
  { v: "all", l: "All pages" },
  { v: "home", l: "Home" },
  { v: "about", l: "About" },
  { v: "contact", l: "Contact" },
  { v: "global", l: "Global (footer, brand)" },
  { v: "charter", l: "Charter" },
  { v: "culture", l: "Culture & Arts" },
  { v: "rebirthing", l: "Rebirthing Genesis" },
  { v: "retreats", l: "Retreats" },
  { v: "dubbi", l: "Cloud Forrest EcoVillage" },
  { v: "donate", l: "Donate" },
  { v: "volunteer", l: "Volunteer" },
];

const TYPES = [
  { v: "text", l: "Text (single line)" },
  { v: "textarea", l: "Text (paragraph)" },
  { v: "image", l: "Image URL" },
  { v: "link", l: "Link URL" },
];

export default function Content() {
  const [blocks, setBlocks] = useState([]);
  const [pageFilter, setPageFilter] = useState("all");
  const [drafts, setDrafts] = useState({}); // id → {value, is_published}
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyBlock, setHistoryBlock] = useState(null);
  const [historyEntries, setHistoryEntries] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const emptyNew = { page_slug: "home", block_key: "", block_type: "text", label: "", value: "" };
  const [newBlock, setNewBlock] = useState(emptyNew);

  const load = async () => {
    const { data } = await api.get("/content/blocks", { params: pageFilter === "all" ? {} : { page_slug: pageFilter } });
    setBlocks(data);
    setDrafts({});
  };
  useEffect(() => { load(); }, [pageFilter]);

  const grouped = useMemo(() => {
    const g = {};
    for (const b of blocks) {
      if (!g[b.page_slug]) g[b.page_slug] = [];
      g[b.page_slug].push(b);
    }
    return g;
  }, [blocks]);

  const dirty = (b) => {
    const d = drafts[b.id];
    if (!d) return false;
    return d.value !== b.value || d.is_published !== b.is_published;
  };

  const setDraft = (b, patch) => {
    setDrafts(prev => ({ ...prev, [b.id]: { value: b.value, is_published: b.is_published, ...prev[b.id], ...patch } }));
  };

  const save = async (b) => {
    const d = drafts[b.id];
    if (!d) return;
    try {
      await api.patch(`/content/blocks/${b.id}`, { value: d.value, is_published: d.is_published });
      toast.success("Saved");
      invalidateContentCache(b.page_slug);
      load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (b) => {
    if (!confirm(`Delete content block "${b.page_slug} · ${b.block_key}"? The public page will fall back to its hardcoded default.`)) return;
    await api.delete(`/content/blocks/${b.id}`);
    invalidateContentCache(b.page_slug);
    load();
    toast.success("Deleted");
  };

  const openHistory = async (b) => {
    setHistoryBlock(b);
    const { data } = await api.get(`/content/blocks/${b.id}/history`);
    setHistoryEntries(data);
    setHistoryOpen(true);
  };

  const createNew = async (e) => {
    e.preventDefault();
    try {
      await api.post("/content/blocks", newBlock);
      toast.success("Block added");
      invalidateContentCache(newBlock.page_slug);
      setNewOpen(false);
      setNewBlock(emptyNew);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed — key may already exist");
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Executive · Content</div>
          <h1 className="font-serif text-4xl mt-1">Public Content Editor</h1>
          <p className="text-ink-soft mt-2 max-w-2xl leading-relaxed">
            Edit copy, images, and links on the public site without engineering. Every change is journaled (Archive Covenant). Empty blocks fall back to the hardcoded default in the component.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={pageFilter} onValueChange={setPageFilter}>
            <SelectTrigger className="w-52" data-testid="content-page-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{PAGES.map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
          </Select>
          <button onClick={() => setNewOpen(true)} data-testid="new-block-btn" className="pill pill-primary text-sm">
            <Plus className="h-4 w-4" /> New Block
          </button>
        </div>
      </div>

      {/* Signal strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile label="Total blocks" value={blocks.length} testId="stat-blocks-total" />
        <StatTile label="Overridden" value={blocks.filter(b => (b.value || "").trim().length > 0).length} testId="stat-blocks-overridden" />
        <StatTile label="Using defaults" value={blocks.filter(b => !((b.value || "").trim().length > 0)).length} testId="stat-blocks-defaults" />
        <StatTile label="Hidden" value={blocks.filter(b => !b.is_published).length} testId="stat-blocks-hidden" />
      </div>

      {/* Groups */}
      <div className="space-y-6">
        {Object.entries(grouped).length === 0 && (
          <div className="text-ink-soft text-sm">No blocks for this filter.</div>
        )}
        {Object.entries(grouped).map(([slug, items]) => (
          <section key={slug} className="bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid={`content-page-${slug}`}>
            <div className="px-5 py-3 border-b border-line bg-bone/40 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h2 className="font-serif text-xl text-ink">{PAGES.find(p => p.v === slug)?.l || slug}</h2>
                <span className="text-xs text-ink-soft">{items.length} block{items.length === 1 ? "" : "s"}</span>
              </div>
              <code className="text-[10px] text-ink-soft">/{slug === "home" ? "" : slug}</code>
            </div>
            <div className="divide-y divide-line">
              {items.map(b => {
                const d = drafts[b.id];
                const currentValue = d?.value ?? b.value;
                const currentPublished = d?.is_published ?? b.is_published;
                const isDirty = dirty(b);
                const hasValue = (currentValue || "").trim().length > 0;
                return (
                  <div key={b.id} className="px-5 py-4 hover:bg-bone/20" data-testid={`content-row-${b.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <code className="text-xs text-forest bg-forest/5 px-2 py-0.5 rounded">{b.block_key}</code>
                          <Badge className="bg-ink-soft/15 text-ink-soft text-[10px] uppercase tracking-widest">{b.block_type}</Badge>
                          {!currentPublished && <Badge className="bg-terracotta/15 text-terracotta text-[10px] uppercase tracking-widest">Hidden</Badge>}
                          {!hasValue && <Badge className="bg-forest/10 text-forest text-[10px] uppercase tracking-widest">Using code default</Badge>}
                        </div>
                        {b.label && <div className="text-sm text-ink mt-1.5">{b.label}</div>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => setDraft(b, { is_published: !currentPublished })}
                          data-testid={`toggle-vis-${b.id}`}
                          className="text-ink-soft hover:text-forest p-1.5"
                          title={currentPublished ? "Hide from public" : "Show on public"}
                        >{currentPublished ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                        <button onClick={() => openHistory(b)} data-testid={`history-${b.id}`} className="text-ink-soft hover:text-forest p-1.5" title="History"><History className="h-4 w-4" /></button>
                        <button onClick={() => del(b)} data-testid={`delete-${b.id}`} className="text-ink-soft hover:text-terracotta p-1.5" title="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="mt-3">
                      {b.block_type === "textarea" ? (
                        <Textarea
                          rows={3}
                          value={currentValue}
                          onChange={(e) => setDraft(b, { value: e.target.value })}
                          placeholder="(empty — public page uses hardcoded default)"
                          data-testid={`value-${b.id}`}
                          className="bg-bone/40"
                        />
                      ) : b.block_type === "image" ? (
                        <ImageBlockEditor
                          value={currentValue}
                          onChange={(v) => setDraft(b, { value: v })}
                          testId={`value-${b.id}`}
                        />
                      ) : (
                        <Input
                          type="text"
                          value={currentValue}
                          onChange={(e) => setDraft(b, { value: e.target.value })}
                          placeholder="(empty — public page uses hardcoded default)"
                          data-testid={`value-${b.id}`}
                          className="bg-bone/40"
                        />
                      )}
                    </div>
                    <div className="mt-3 flex items-center justify-between">
                      <div className="text-[10px] text-ink-soft">
                        Last updated {b.updated_at ? dayjs(b.updated_at).format("MMM D, YYYY · HH:mm") : "—"}{b.updated_by ? ` · by ${b.updated_by}` : ""}
                      </div>
                      <button
                        onClick={() => save(b)}
                        disabled={!isDirty}
                        data-testid={`save-${b.id}`}
                        className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest px-3 py-1.5 rounded-md transition-colors ${isDirty ? "bg-forest text-bone hover:bg-forest/90" : "bg-line text-ink-soft cursor-not-allowed"}`}
                      >
                        <Save className="h-3.5 w-3.5" /> {isDirty ? "Save" : "Saved"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-bone max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Version history</DialogTitle>
            <DialogDescription>
              {historyBlock ? <code className="text-forest">{historyBlock.page_slug} · {historyBlock.block_key}</code> : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto divide-y divide-line">
            {historyEntries.length === 0 && <div className="text-ink-soft text-sm py-6">No prior revisions — this block has not been changed since creation.</div>}
            {historyEntries.map(h => (
              <div key={h.id} className="py-4" data-testid={`history-entry-${h.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-ink-soft">
                  {dayjs(h.updated_at).format("MMM D, YYYY · HH:mm")}{h.updated_by ? ` · ${h.updated_by}` : ""}
                </div>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10px] text-terracotta uppercase tracking-widest mb-1">Previous</div>
                    <div className="text-sm text-ink-soft bg-bone/50 border border-line rounded p-2 whitespace-pre-wrap break-words">{h.previous_value || <span className="italic text-ink-soft/60">(empty — was using code default)</span>}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-forest uppercase tracking-widest mb-1">New</div>
                    <div className="text-sm text-ink bg-bone/50 border border-line rounded p-2 whitespace-pre-wrap break-words">{h.new_value || <span className="italic text-ink-soft/60">(empty — falls back to code default)</span>}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* New block dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-bone max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Register a new content block</DialogTitle>
            <DialogDescription className="text-ink-soft">
              Blocks let admins edit public copy without touching code. Use dotted keys like <code>hero.title</code> or <code>section.body</code>.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createNew} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Page</Label>
                <Select value={newBlock.page_slug} onValueChange={(v) => setNewBlock({ ...newBlock, page_slug: v })}>
                  <SelectTrigger data-testid="nb-page"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAGES.filter(p => p.v !== "all").map(p => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline">Type</Label>
                <Select value={newBlock.block_type} onValueChange={(v) => setNewBlock({ ...newBlock, block_type: v })}>
                  <SelectTrigger data-testid="nb-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map(t => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="overline">Block key</Label>
              <Input required data-testid="nb-key" placeholder="hero.title" value={newBlock.block_key} onChange={(e) => setNewBlock({ ...newBlock, block_key: e.target.value })} />
            </div>
            <div>
              <Label className="overline">Label (shown in admin only)</Label>
              <Input data-testid="nb-label" value={newBlock.label} onChange={(e) => setNewBlock({ ...newBlock, label: e.target.value })} />
            </div>
            <div>
              <Label className="overline">Value (optional — leave empty to use code default)</Label>
              {newBlock.block_type === "textarea" ? (
                <Textarea rows={3} data-testid="nb-value" value={newBlock.value} onChange={(e) => setNewBlock({ ...newBlock, value: e.target.value })} />
              ) : (
                <Input data-testid="nb-value" value={newBlock.value} onChange={(e) => setNewBlock({ ...newBlock, value: e.target.value })} />
              )}
            </div>
            <button type="submit" data-testid="nb-submit" className="pill pill-primary mt-2">Register block</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatTile({ label, value, testId }) {
  return (
    <div className="bg-admin-surface border border-line rounded-xl p-4" data-testid={testId}>
      <div className="overline text-ink-soft">{label}</div>
      <div className="font-serif text-3xl text-ink mt-1">{value}</div>
    </div>
  );
}

/**
 * Image block editor — renders the current image preview, a "Choose from
 * Media Library" button that opens the MediaPicker, and a URL input for
 * pasting external URLs (Unsplash, etc.). Writes the chosen URL back to the
 * block value via onChange.
 */
function ImageBlockEditor({ value, onChange, testId }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <div className="space-y-2">
      {value && (
        <div className="rounded-md overflow-hidden border border-line bg-bone/40 max-w-md">
          <img src={value} alt="Current" className="w-full h-40 object-cover" onError={(e) => { e.target.style.display = "none"; }} />
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          data-testid={`${testId}-picker-btn`}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-full bg-forest text-bone hover:bg-ink transition-colors duration-150"
        >
          <FolderOpen className="h-3.5 w-3.5" strokeWidth={1.5} /> Choose from Media Library
        </button>
        <span className="text-xs text-ink-soft">or paste a URL:</span>
      </div>
      <Input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://…"
        data-testid={testId}
        className="bg-bone/40"
      />
      {value && (
        <div className="flex items-center gap-2 text-xs text-ink-soft">
          <ImageIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
          <a href={value} target="_blank" rel="noreferrer" className="underline">Open image in new tab</a>
        </div>
      )}
      <MediaPicker
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(asset) => onChange(asset.url)}
        requirePublic={true}
      />
    </div>
  );
}
